import type {
  ChangeOrder,
  ChangeOrderLineItem,
  ChangeOrderStatus,
  JobScopeHistory,
  LineItemModification,
} from '@/types/changeOrder';
import type { Job } from '@/types/job';
import type { CompanySettings, TeamUser } from '@/types/settings';
import type { ChangeOrderPdfData } from '@/components/documents/change-orders/ChangeOrderPDF';

type ScopeItem = Partial<ChangeOrderLineItem> & Record<string, unknown>;

const STATUS_LABEL: Record<ChangeOrderStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED: 'Approved',
  REJECTED: 'Declined',
  APPLIED: 'Approved',
  VOIDED: 'Void',
};

const PRICING_FIELDS: Array<keyof ChangeOrderLineItem> = [
  'quantity',
  'manualPriceOverride',
  'basePrice',
  'laborRate',
  'priceStrategy',
  'discountAmount',
  'discountPercent',
  'taxable',
  'lineSubtotal',
  'lineTax',
  'lineTotal',
];

function coLabel(changeOrder: ChangeOrder): string {
  return `CO-${String(changeOrder.changeOrderNumber).padStart(4, '0')}`;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function hasNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function fmtMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function fmtScalar(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (value === null || value === undefined || value === '') return 'not set';
  return String(value).replaceAll('_', ' ');
}

function displayNameForUser(idOrName?: string | null, users: TeamUser[] = []): string | null {
  if (!idOrName) return null;
  const found = users.find((user) => user.id === idOrName);
  if (!found) return idOrName;
  return [found.firstName, found.lastName].filter(Boolean).join(' ') || found.email || idOrName;
}

function lineItemIds(item?: ScopeItem | Partial<ChangeOrderLineItem> | null): string[] {
  if (!item) return [];
  const raw = item as Record<string, unknown>;
  return [
    cleanString(raw.lineItemId),
    cleanString(raw.itemId),
    cleanString(raw.snapshotId),
    cleanString(raw.id),
  ].filter(Boolean);
}

function findScopeItem(items: ScopeItem[], id?: string | null): ScopeItem | null {
  if (!id) return null;
  return items.find((item) => lineItemIds(item).includes(id)) ?? null;
}

function buildScopeItems(
  scopeHistory?: JobScopeHistory | null,
  job?: Job | null,
  changeOrder?: ChangeOrder | null,
): ScopeItem[] {
  const jobItems = Array.isArray(job?.lineItems) ? (job.lineItems as ScopeItem[]) : [];
  const scopeHistoryChangeOrders = Array.isArray(scopeHistory?.changeOrders)
    ? scopeHistory.changeOrders
    : [];

  return [
    ...((scopeHistory?.originalLineItems ?? []) as ScopeItem[]),
    ...((scopeHistory?.currentLineItems ?? []) as ScopeItem[]),
    ...(scopeHistoryChangeOrders.flatMap((entry) => [
      ...((entry.lineItemsAdded ?? []) as ScopeItem[]),
      ...((entry.lineItemsRemovedSnapshot ?? []) as ScopeItem[]),
      ...((entry.lineItemsModified ?? []).flatMap((modification) =>
        modification.original ? [modification.original as ScopeItem] : [],
      ) as ScopeItem[]),
    ]) as ScopeItem[]),
    ...((changeOrder?.lineItemsRemovedSnapshot ?? []) as ScopeItem[]),
    ...((changeOrder?.lineItemsModified ?? []).flatMap((modification) =>
      modification.original ? [modification.original as ScopeItem] : [],
    ) as ScopeItem[]),
    ...jobItems,
  ];
}

function effectiveUnitPrice(item: Partial<ChangeOrderLineItem>): number {
  const strategy = cleanString(item.priceStrategy).toUpperCase();
  if (item.manualPriceOverride != null) return asNumber(item.manualPriceOverride);
  if (strategy.includes('TIME') || strategy.includes('T&M') || strategy.includes('T_AND_M')) {
    return asNumber(item.basePrice) + asNumber(item.laborRate);
  }
  return asNumber(item.basePrice ?? item.laborRate ?? item.lineSubtotal);
}

function calculateLineTotal(item: Partial<ChangeOrderLineItem>, effectiveTaxRate = 0): number {
  const qty = asNumber(item.quantity, 1);
  const price = effectiveUnitPrice(item);
  const subtotal = qty * price;
  const discountAmount = asNumber(item.discountAmount, 0);
  const discountPercent = asNumber(item.discountPercent, 0);
  const percentDiscount = discountPercent > 0 ? (subtotal * discountPercent) / 100 : 0;
  const discountSavings = Math.min(subtotal, percentDiscount + Math.max(0, discountAmount));
  const afterDiscount = subtotal - discountSavings;
  const lineTax = item.taxable ? afterDiscount * effectiveTaxRate : 0;
  return Math.round((afterDiscount + lineTax) * 100) / 100;
}

function lineItemTotal(item?: Partial<ChangeOrderLineItem> | null): number {
  if (!item) return 0;
  if (hasNumber(item.lineTotal)) return item.lineTotal;
  if (hasNumber(item.lineSubtotal)) return item.lineSubtotal + asNumber(item.lineTax, 0);
  return calculateLineTotal(item);
}

function lineItemMeta(item?: Partial<ChangeOrderLineItem> | null): string {
  if (!item) return '';
  const type = item.type ? String(item.type).replaceAll('_', ' ') : 'Item';
  const qty = asNumber(item.quantity, 1);
  const unit = item.unit || 'each';
  return `${type} · ${qty} ${unit}`;
}

function shouldRecalculateModifiedTotal(updated: Partial<ChangeOrderLineItem>): boolean {
  return (
    PRICING_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(updated, field)) &&
    !Object.prototype.hasOwnProperty.call(updated, 'lineTotal')
  );
}

function deriveEffectiveTaxRate(item: Partial<ChangeOrderLineItem>): number {
  if (!item.taxable) return 0;
  const lineSubtotal = asNumber(item.lineSubtotal);
  const discountPercent = asNumber(item.discountPercent);
  const discountAmount = asNumber(item.discountAmount);
  const percentDiscount = discountPercent > 0 ? (lineSubtotal * discountPercent) / 100 : 0;
  const taxableBase = lineSubtotal - Math.min(lineSubtotal, percentDiscount + discountAmount);
  const lineTax = asNumber(item.lineTax);

  if (taxableBase <= 0 || lineTax <= 0) return 0;
  return lineTax / taxableBase;
}

function mergeLineItem(
  lineItemId: string,
  original?: Partial<ChangeOrderLineItem>,
  updated?: Partial<ChangeOrderLineItem>,
): Partial<ChangeOrderLineItem> {
  return {
    lineItemId,
    name: original?.name ?? updated?.name ?? lineItemId,
    type: original?.type ?? updated?.type ?? 'Item',
    quantity: original?.quantity ?? updated?.quantity ?? 1,
    ...original,
    ...updated,
  };
}

function buildModifiedItem(modification: LineItemModification, scopeItems: ScopeItem[]) {
  const fallback = findScopeItem(scopeItems, modification.lineItemId) ?? {};
  const original = mergeLineItem(modification.lineItemId, fallback, modification.original);
  const updated = mergeLineItem(modification.lineItemId, original, modification.updated);
  const originalTotal = lineItemTotal(original);
  const updatedTotal = shouldRecalculateModifiedTotal(modification.updated)
    ? calculateLineTotal(updated, deriveEffectiveTaxRate(original))
    : lineItemTotal(updated);
  const changes: ChangeOrderPdfData['modifiedItems'][number]['changes'] = [];

  const originalQty = asNumber(original.quantity, 1);
  const updatedQty = asNumber(updated.quantity, originalQty);
  if (originalQty !== updatedQty) {
    changes.push({
      label: 'Quantity',
      before: fmtScalar(originalQty),
      after: fmtScalar(updatedQty),
    });
  }

  const originalPrice = effectiveUnitPrice(original);
  const updatedPrice = effectiveUnitPrice(updated);
  if (originalPrice !== updatedPrice) {
    changes.push({
      label: 'Price',
      before: fmtMoney(originalPrice),
      after: fmtMoney(updatedPrice),
    });
  }

  const originalBilling = original.priceStrategy;
  const updatedBilling = updated.priceStrategy;
  if (originalBilling !== updatedBilling) {
    changes.push({
      label: 'Billing',
      before: fmtScalar(originalBilling),
      after: fmtScalar(updatedBilling),
    });
  }

  if (original.taxable !== updated.taxable) {
    changes.push({
      label: 'Taxable',
      before: fmtScalar(original.taxable),
      after: fmtScalar(updated.taxable),
    });
  }

  if (original.description !== updated.description) {
    changes.push({
      label: 'Description',
      before: fmtScalar(original.description),
      after: fmtScalar(updated.description),
    });
  }

  return {
    name: String(updated.name || original.name || 'Line item'),
    originalTotal,
    updatedTotal,
    delta: updatedTotal - originalTotal,
    changes,
  };
}

function buildPdfData(args: {
  changeOrder: ChangeOrder;
  job?: Job | null;
  scopeHistory?: JobScopeHistory | null;
  users?: TeamUser[];
  company?: CompanySettings | null;
  timeZone?: string | null;
}): ChangeOrderPdfData {
  const { changeOrder, job, scopeHistory, users = [], company, timeZone } = args;
  const scopeItems = buildScopeItems(scopeHistory, job, changeOrder);
  const removedItems = changeOrder.lineItemsRemoved.map((id) => {
    const item =
      findScopeItem(changeOrder.lineItemsRemovedSnapshot as ScopeItem[], id) ??
      findScopeItem(scopeItems, id);
    return {
      name: String(item?.name || 'Line item'),
      meta: lineItemMeta(item),
      description: typeof item?.description === 'string' ? item.description : undefined,
      amount: lineItemTotal(item),
    };
  });
  const modifiedItems = changeOrder.lineItemsModified.map((modification) =>
    buildModifiedItem(modification, scopeItems),
  );
  const added = changeOrder.lineItemsAdded.reduce((sum, item) => sum + lineItemTotal(item), 0);
  const removed = removedItems.reduce((sum, item) => sum + item.amount, 0);
  const modified = modifiedItems.reduce((sum, item) => sum + item.delta, 0);
  const currentJobTotal = asNumber(job?.grandTotal, 0);
  const newJobTotal =
    changeOrder.status === 'APPLIED'
      ? currentJobTotal
      : currentJobTotal + changeOrder.priceImpact.totalDelta;

  return {
    number: coLabel(changeOrder),
    status: STATUS_LABEL[changeOrder.status],
    createdAt: changeOrder.createdAt,
    timeZone,
    jobNumber: job?.jobNumber,
    jobName: job?.name || (typeof job?.jobType === 'string' ? job.jobType : job?.jobType?.name),
    reason: changeOrder.reason || changeOrder.description,
    addedItems: changeOrder.lineItemsAdded.map((item) => ({
      name: item.name || 'Line item',
      meta: lineItemMeta(item),
      description: item.description,
      amount: lineItemTotal(item),
    })),
    removedItems,
    modifiedItems,
    financial: {
      added,
      removed,
      modified,
      tax: asNumber(changeOrder.priceImpact.taxDelta, 0),
      net: asNumber(changeOrder.priceImpact.totalDelta, 0),
      newJobTotal,
    },
    approval: {
      method: formatApprovalMethod(changeOrder.approvalMethod),
      signatureUrl: changeOrder.approvalSignatureUrl,
      requestedBy:
        changeOrder.requestedByName ?? displayNameForUser(changeOrder.requestedBy, users),
      requestedAt: changeOrder.createdAt,
      approvedBy: changeOrder.approvedByName ?? displayNameForUser(changeOrder.approvedBy, users),
      approvedAt: changeOrder.approvedAt,
      rejectedBy: changeOrder.rejectedByName ?? displayNameForUser(changeOrder.rejectedBy, users),
      rejectedAt: changeOrder.rejectedAt,
      rejectionReason: changeOrder.rejectionReason,
      appliedAt: changeOrder.appliedAt,
    },
    company: {
      name: company?.name,
      logoUrl: company?.logoUrl ?? null,
      lines: [
        [
          company?.address?.street,
          company?.address?.city,
          company?.address?.state,
          company?.address?.zip,
        ]
          .filter(Boolean)
          .join(', '),
      ],
    },
  };
}

function formatApprovalMethod(method?: string | null): string | null {
  const normalized = cleanString(method).toUpperCase();
  if (!normalized) return null;
  if (normalized === 'SIGNATURE') return 'Signature';
  if (normalized === 'ONLINE') return 'Online';
  if (normalized === 'EMAIL') return 'Email';
  if (normalized === 'VERBAL') return 'Verbal';
  return normalized
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadChangeOrderPdf(args: {
  changeOrder: ChangeOrder;
  job?: Job | null;
  scopeHistory?: JobScopeHistory | null;
  users?: TeamUser[];
  company?: CompanySettings | null;
  timeZone?: string | null;
}) {
  const [{ pdf }, { ChangeOrderPDFDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('@/components/documents/change-orders/ChangeOrderPDF'),
  ]);
  const data = buildPdfData(args);
  const blob = await pdf(<ChangeOrderPDFDocument data={data} />).toBlob();
  triggerDownload(blob, `${data.number.toLowerCase()}.pdf`);
}
